"use client";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { MdKeyboardArrowRight, MdKeyboardArrowLeft } from "react-icons/md";
import { Pause, Play } from "@/components/Icons/Icons";

interface DataItem {
  title: string;
  description: string;
  icon: string;
}

const FunctionDepartment = ({ title, functionDeprtmentData }: { title: string; functionDeprtmentData: DataItem[] }) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isPlay, setIsPlay] = useState(true);
  const [progress, setProgress] = useState(0);

  const autoplayDelay = 3000; // Swiper autoplay delay in ms
  const CIRCUMFERENCE = 138; // 2πr, where r=22
  const progressRef = useRef(0); // local ref for smooth progress

  // Handle play/pause toggle
  const togglePlayPause = () => {
    if (swiperRef.current) {
      if (isPlay) {
        swiperRef.current.autoplay.stop();
      } else {
        swiperRef.current.autoplay.start();
        progressRef.current = 0;
        setProgress(0);
      }
      setIsPlay(!isPlay);
    }
  };

  // Sync progress with autoplay
  const handleAutoplayTimeLeft = (_swiper: SwiperType, time: number) => {
    const elapsed = autoplayDelay - time;
    const percent = Math.min((elapsed / autoplayDelay) * 100, 100);
    progressRef.current = percent;
    requestAnimationFrame(() => setProgress(progressRef.current));
  };

  // Reset progress on slide change
  const onSlideChange = () => {
    progressRef.current = 0;
    setProgress(0);
  };

  return (
    <section className="lg:ml-16 pt-24 pb-10 lg:py-24 xl:py-24 xl:ml-60">
      {title && (
        <h1 className="text-3xl md:text-[40px] lg2:text-5xl xl:text-6xl leading-[1.2] font-bold text-center text-[#1D1D1F] pb-6 xl:pb-22">{title}</h1>
      )}

      <Swiper
        modules={[Autoplay]}
        autoplay={{ delay: autoplayDelay, disableOnInteraction: false }}
        spaceBetween={20}
        slidesPerView={1}
        loop={true}
        breakpoints={{
          640: { slidesPerView: 1, spaceBetween: 20 },
          768: { slidesPerView: 2, spaceBetween: 20 },
          1024: { slidesPerView: 4, spaceBetween: 25 },
        }}
        className="mySwiper"
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          // Ensure autoplay starts immediately when Swiper is ready
          try {
            if (swiper?.autoplay && !swiper.autoplay.running) {
              swiper.autoplay.start();
              setIsPlay(true);
              progressRef.current = 0;
              setProgress(0);
            }
          } catch (e) {
            // ignore if autoplay API isn't available yet
            // console.warn('Autoplay start failed', e);
          }
        }}
        onSlideChange={onSlideChange}
        onAutoplayTimeLeft={handleAutoplayTimeLeft}
      >
        {functionDeprtmentData?.map((item, index) => (
        <SwiperSlide key={index} className="!h-auto">
  <div className="h-full pt-6 pb-3 px-6 bg-white rounded-2xl flex flex-col">
    <div className="mb-4">
      <Image src={item.icon} alt="Icon" width={30} height={30} />
    </div>

    <h2 className="text-xl font-semibold text-gray-900 mb-2">
      {item.title}
    </h2>

    <p className="text-sm text-gray-600 flex-grow">
      {item.description}
    </p>
  </div>
</SwiperSlide>

        ))}
      </Swiper>

      <div className="flex justify-between items-center mt-9 me-8 gap-4">
        {/* Play/Pause Button with Progress Circle */}
        <div className="relative lg:pe-5 md:pb-0 md:pe-3 lg:pb-0 cursor-pointer" onClick={togglePlayPause}>
        
           
              <button className="w-full h-full cursor-pointer flex items-center justify-center" aria-label={isPlay ? "Pause" : "Play"}>
                {isPlay ? <Pause /> : <Play />}
              </button>
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-2">
          <button
            aria-label="Previous Slide"
            onClick={() => swiperRef.current?.slidePrev()}
            className="relative z-[1] lg:w-[36px] text-3xl text-[#616165] cursor-pointer lg:h-[36px] w-[27px] h-[27px] rounded-full bg-gray-100 flex items-center justify-center disabled:opacity-50"
          >
            <MdKeyboardArrowLeft />
          </button>
          <button
            aria-label="Next Slide"
            onClick={() => swiperRef.current?.slideNext()}
            className="relative z-[1] lg:w-[36px] text-3xl text-[#616165] cursor-pointer lg:h-[36px] w-[27px] h-[27px] rounded-full bg-gray-100 flex items-center justify-center disabled:opacity-50"
          >
            <MdKeyboardArrowRight />
          </button>
        </div>
      </div>
  
    </section>
  );
};

export default FunctionDepartment;
